import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Native American Lands | Natural Resource Production",
  "includeToc": true,
  "description": "The process of extracting natural resources on Native American land is complex. There are many stakeholders involved in the process, which starts with planning and ends with reclaiming.",
  "tags": ["How revenue works", "production", "renewable energy", "geothermal", "wind", "water power", "biomass"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/dec-24-uploads/how-revenue-works/"
      }}>{`How revenue works`}</a>{` /`}</p>

    <h1 {...{
      "id": "Natural-Resource-Production-on-Native-American-Land",
      "style": {
        "position": "relative"
      }
    }}>{`Natural Resource Production on Native American Land`}</h1>
    <p>{`The process of extracting natural resources on Native American land is complex. It starts with planning and ends with reclaiming. Many stakeholders are involved throughout the entire process.`}</p>
    <br />
This overview is based on the <a href="https://onrr.gov/about/interagency?tabs=standard-operating-procedures-sops">Onshore Energy and Mineral Lease Management Interagency Standard Operating Procedures <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>.
    <h2 {...{
      "id": "Leasing-process-for-mineral-resources",
      "style": {
        "position": "relative"
      }
    }}>{`Leasing process for mineral resources`}</h2>
    <p>{`Several agencies within the Department of the Interior play a role in the process of developing oil, gas, coal, or nonenergy minerals on Native American land:`}</p>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.bia.gov/"
        }}>{`Bureau of Indian Affairs `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (BIA)`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.blm.gov/"
        }}>{`Bureau of Land Management `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (BLM)`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://onrr.gov/"
        }}>{`Office of Natural Resources Revenue `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (ONRR)`}</li>
      <li parentName="ul">{`The Office of the Special Trustee for American Indians (OST)`}</li>
      <li parentName="ul">{`The Office of Indian Energy and Economic Development (IEED) and its `}<a parentName="li" {...{
          "href": "https://www.bia.gov/bia/ots/demd"
        }}>{`Division of Energy and Mineral Development `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (DEMD)`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.osmre.gov/"
        }}>{`Office of Surface Mining Reclamation and Enforcement `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (OSMRE)`}</li>
    </ul>
    <p>{`Key documents and resources:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.epa.gov/laws-regulations/summary-national-environmental-policy-act"
        }}>{`National Environmental Policy Act `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (NEPA)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.bia.gov/sites/bia.gov/files/assets/as-ia/ieed/ieed/pdf/idc010987.pdf"
        }}>{`Sample minerals development agreement (PDF) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://bia.gov/sites/bia.gov/files/assets/as-ia/ieed/ieed/pdf/idc010163.pdf"
        }}>{`Sample aggregate minerals lease (PDF) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul">{`DEMD resources:`}<ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/bia/ots/demd/publications-lease-samples"
            }}>{`Sample mineral leases `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/bia/ots/demd/fluid-minerals"
            }}>{`Oil and gas `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/bia/ots/demd/solid-minerals"
            }}>{`Minerals `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/service/grant-assistance-energy-grants"
            }}>{`Information on tribes actively seeking mineral development on their reservations `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
        </ul></li>
    </ul>
    <h3 {...{
      "id": "Oil-and-gas",
      "style": {
        "position": "relative"
      }
    }}>{`Oil and gas`}</h3>
    <h4 {...{
      "id": "Plan",
      "style": {
        "position": "relative"
      }
    }}>{`Plan`}</h4>
    <p>{`With the concurrence of the Native American trust beneficiary, BIA develops and maintains a list of leasable tracts that have mineral potential. They use company tract nominations, mineral potential assessment findings, lease history, and other factors to identify the leasable tracts. `}<GlossaryTerm mdxType="GlossaryTerm">{`BLM`}</GlossaryTerm>{` conducts mineral potential assessments if requested by either BIA or IEED. DEMD provides `}<a href="https://www.bia.gov/bia/ots/demd/oil-gas-plays">{`atlases of oil and gas plays `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a> on reservations. The <a href="https://www.bia.gov/bia/ots/demd">Energy and Mineral Development Program <OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` funds energy and mineral pre-development studies for tribes.`}</p>
    <br />
BLM assesses a fair market value recommendation for the oil & gas and provides that to BIA, who ultimately manages any lease sale. This includes a <GlossaryTerm mdxType="GlossaryTerm">royalty</GlossaryTerm> rate, <GlossaryTerm mdxType="GlossaryTerm">rental</GlossaryTerm> rate, and minimum <GlossaryTerm mdxType="GlossaryTerm">bonus</GlossaryTerm>. If there is to be a lease sale, BIA then drafts the lease terms with BLM and ONRR providing comments. BIA, with BLM as a technical advisor, is responsible for NEPA documents necessary to support leasing.
    <h4 {...{
      "id": "Lease",
      "style": {
        "position": "relative"
      }
    }}>{`Lease`}</h4>
    <p>{`BIA manages the sale of a lease. It provides notice of an upcoming sale to the public, negotiates and accepts the lease bid (including the bonus), and approves or disapproves the lease, with the required consent of the Native American mineral owners. BIA grants rights-of-way across trust properties. It also generally approves surface-use agreements necessary for the execution of the lease. During this process, BIA will provide relevant information to BLM and ONRR. BIA may request information on the lessee from both agencies. BIA, with advice from BLM, will also determine the bond the company must provide as security for the lease.  Before approving the lease, BIA may ask BLM and ONRR to report whether the operator is compliant for other properties.`}</p>
    <br />
If the lease occurs as a part of a minerals development agreement between the operator and the tribe, then IEED, upon request of the tribe, leads the agreement review. BIA, BLM, and ONRR support the review. The Secretary then determines if the agreement is in the “best interest” of the Native American mineral owner(s). The agreement is then approved or disapproved.
    <h4 {...{
      "id": "Explore",
      "style": {
        "position": "relative"
      }
    }}>{`Explore`}</h4>
    <p>{`After BIA issues the oil and gas lease, exploration can begin. It can be on a lease basis, under a formal federal exploratory agreement, or under an `}<a parentName="p" {...{
        "href": "/sites/dec-24-uploads/how-revenue-works/native-american-ownership-governance/#indian-mineral-development-act-of-1982"
      }}>{`Indian Mineral Development Agreement`}</a>{`. Development on a lease basis is similar to an application for permit to drill (APD).`}</p>
    <br />
For more about APDs, <a href="/how-revenue-works/onshore-oil-gas/">learn more about the process of developing oil and gas on federal land</a>.
    <h4 {...{
      "id": "Develop-decommission-and-reclaim",
      "style": {
        "position": "relative"
      }
    }}>{`Develop, decommission, and reclaim`}</h4>
    <p>{`BLM regulates the development, decommissioning, and reclamation of wells. Before drilling, the lease holder must submit an `}<GlossaryTerm mdxType="GlossaryTerm">{`APD`}</GlossaryTerm>{` to BLM. BLM approves or denies permits and oversees and accepts reclamation of the lease. BLM and BIA determine together their roles and responsibilities to ensure that the APD meets NEPA requirements.`}</p>
    <br />
BLM performs inspections and operation-related enforcement. It also authorizes development and drilling operations. The authorizations might include disposal of wastewater, venting or flaring, or well-deepening.  BLM might also determine if production has been avoidably lost or will drain oil from underneath neighboring lands. BLM and ONRR advise BIA on gas storage agreements as necessary.
    <br /><br />
When production occurs, BLM notifies BIA and ONRR. ONRR collects production data and provides that data to BLM. ONRR also collects payments from the operator and, if applicable, co-lease holders. As production continues, ONRR collects and verifies royalties and rentals from the operator. ONRR distributes revenues according to the law.
    <br /><br />
Read more about <a href="/how-revenue-works/native-american-revenue/">revenue collection and distribution</a>.
    <br /><br />
BIA, BLM, and ONRR share information on any changes to the lease or operations. BLM can initiate, and BIA or ONRR can request, an order to cease production. BIA can cancel a lease in coordination with BLM and ONRR. ONRR can request that BIA cancel a lease.  BLM (for operations) or ONRR (for royalty or bankruptcy) notifies BIA if it is necessary to call an operator’s lease bond.
    <br /><br />
During decommissioning and reclamation, BLM monitors the lease holder’s compliance. Before final acceptance of the reclamation, BIA can inspect the area with BLM.
    <br /><br />
A mineral trust owner may wish to assume the operation or ownership of a well. If they do, BLM provides advice on the condition of the well, estimated remaining production, the cost of abandonment and reclamation, and a value to the trust owner. ONRR provides BIA with the current account of the operator’s payments. If there is a change in trust status, <a href="/how-revenue-works/native-american-ownership-governance/">BIA notifies ONRR of new fractional interests or other changes</a>.
    <h3 {...{
      "id": "Nonenergy-minerals",
      "style": {
        "position": "relative"
      }
    }}>{`Nonenergy minerals`}</h3>
    <h4 {...{
      "id": "Plan-1",
      "style": {
        "position": "relative"
      }
    }}>{`Plan`}</h4>
    <p>{`Planning begins when either BIA or IEED requests that BLM evaluates the mineral potential of specific trust lands. If mineral potential exists, BLM analyzes tracts available for lease. BIA identifies which unleased tracts can be leased. The `}<a parentName="p" {...{
        "href": "https://www.bia.gov/service/grant-assistance-energy-grants"
      }}>{`Energy and Mineral Development Program funds energy and mineral pre-development studies for tribes `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <br />
BLM assesses a fair market value recommendation for the mineral and provides that to BIA, which manages any lease sale. The recommendation includes a royalty rate, rental rate, and minimum bonus. If there is to be a lease sale, BIA drafts the lease terms with BLM and ONRR providing comments. BIA, with BLM as a technical advisor, handles any necessary NEPA documents.
    <h4 {...{
      "id": "Lease-1",
      "style": {
        "position": "relative"
      }
    }}>{`Lease`}</h4>
    <p>{`BIA manages the sale of a lease. It provides notice of an upcoming sale to the public, negotiates and accepts the lease bid (including the bonus), and approves or disapproves the lease, with the required consent of the Native American mineral owners. BIA grants rights-of-way across trust properties. It also generally approves surface-use agreements necessary for the execution of the lease. During this process, BIA will provide relevant information to BLM and ONRR. BIA may request information on the lessee from both agencies. BIA, with advice from BLM, will also determine the bond the company must provide as security for the lease.  Before approving the lease, BIA may ask BLM and ONRR to report whether the operator is compliant for other properties.`}</p>
    <br />
If the lease occurs as a part of a minerals development agreement between the operator and the tribe, then IEED, upon request of the tribe, leads the agreement review. BIA, BLM, and ONRR support the review. The Secretary then determines if the agreement is in the “best interest” of the Native American mineral owner(s). The agreement is then approved or disapproved.
    <h4 {...{
      "id": "Explore-1",
      "style": {
        "position": "relative"
      }
    }}>{`Explore`}</h4>
    <p>{`BLM approves all exploration plans for trust lands. If the contract requires consultation, BLM consults with BIA and the Native American mineral owner before approving the exploration plan. For allotted lands, BLM consults with BIA. As the responsible agency, BLM must ensure that the exploration plan complies with NEPA.`}</p>
    <h4 {...{
      "id": "Develop-decommission-and-reclaim-1",
      "style": {
        "position": "relative"
      }
    }}>{`Develop, decommission, and reclaim`}</h4>
    <p>{`BLM regulates the development and reclamation of mines. Before mining, the lease holder must submit a mine plan to BLM. BIA conducts an on-site inspection and BLM approves or denies the mine plans. BLM is also responsible for the administration and acceptance of the lease reclamation. BLM and BIA determine together their roles and responsibilities to ensure that the plan meets NEPA requirements.`}</p>
    <br />
BLM performs inspections and operation-related enforcement, including the disposal of mine waste.
    <br />
When production occurs, BLM notifies BIA and ONRR.  ONRR collects production data and provides that data to BLM. ONRR also collects payments from the operator.  As production continues, BIA collects, accounts for, and deposits royalties and rentals. ONRR will distribute revenues according to the law.
    <br /><br />
Read more about <a href="/how-revenue-works/native-american-revenue/">revenue collection and distribution</a>.
    <br /><br />
BIA, BLM, and ONRR share information on any changes to the lease or operations. BLM can initiate, and BIA or ONRR can request, an order to cease production. BIA can cancel a lease in coordination with BLM and ONRR. ONRR can request that BIA cancel a lease. BLM (for operations) or ONRR (for royalty or bankruptcy) notifies BIA if it is necessary to call an operator’s lease bond.
    <br /><br />
During decommissioning and reclamation, BLM monitors the lease holder’s compliance. Before final acceptance of the reclamation, BIA can inspect the area with BLM.
    <br /><br />
A mineral trust owner may wish to assume the operation or ownership of a mine. If they do, BLM provides advice on the condition of the mine, estimated remaining production, the cost of abandonment and reclamation, and a value to the trust owner. ONRR provides BIA with the current account of the operator’s payments. If there is a change in trust status, <a href="/how-revenue-works/native-american-ownership-governance/">BIA notifies ONRR of new fractional interests or other changes</a>.
    <h3 {...{
      "id": "Coal",
      "style": {
        "position": "relative"
      }
    }}>{`Coal`}</h3>
    <h4 {...{
      "id": "Plan-2",
      "style": {
        "position": "relative"
      }
    }}>{`Plan`}</h4>
    <p>{`Planning begins when either BIA or IEED requests that BLM evaluates the mineral potential of specific trust lands. If mineral potential exists, BLM analyzes tracts available for lease. BIA identifies which unleased tracts can be leased. The `}<a parentName="p" {...{
        "href": "https://www.bia.gov/service/grant-assistance-energy-grants"
      }}>{`Energy and Mineral Development Program funds energy and mineral pre-development studies for tribes `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <br />
BLM assesses a fair market value recommendation for the coal and provides that to BIA, which manages any lease sale. The recommendation includes a royalty rate, rental rate, and minimum bonus. If there is to be a lease sale, BIA drafts the lease terms with BLM and ONRR providing comments. BIA, with BLM as a technical advisor, handles any necessary NEPA documents.
    <h4 {...{
      "id": "Explore-2",
      "style": {
        "position": "relative"
      }
    }}>{`Explore`}</h4>
    <p>{`Exploration begins when BIA receives a prospecting permit application or a request for help from Native American mineral owner(s). BIA approves or denies the permit and establishes a performance bond. BLM approves or denies an exploration plan. BIA then monitors compliance with the prospecting permit. BLM monitors compliance with the exploration plan. More exploration can occur after the issuance of a lease and under later exploration plans.`}</p>
    <h4 {...{
      "id": "Lease-2",
      "style": {
        "position": "relative"
      }
    }}>{`Lease`}</h4>
    <p>{`The Native American mineral owner and the applicant then enter into a minerals agreement or a lease. If the mining will occur as a part of a minerals agreement under the Indian Mineral Development Act, BIA, BLM, and `}<GlossaryTerm mdxType="GlossaryTerm">{`OSMRE`}</GlossaryTerm>{` provide technical assistance during negotiation. BIA, BLM, and OSMRE then review the agreement so the Secretary of the Interior can approve or deny the agreement.`}</p>
    <br />
BLM provides appraisal and bond recommendations to BIA. BIA makes sure that the leasing process complies with NEPA.
    <h4 {...{
      "id": "Develop-decommission-and-reclaim-2",
      "style": {
        "position": "relative"
      }
    }}>{`Develop, decommission, and reclaim`}</h4>
    <p>{`To begin mining, the operator submits a permit application to the Native American regulatory authority or OSMRE. This occurs under laws and regulations established by the `}<a parentName="p" {...{
        "href": "https://www.osmre.gov/laws-and-regulations/chronology-of-major-smrca-related-events"
      }}>{`Surface Mining Control and Reclamation Act (SMCRA) of 1977 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. BIA, BLM, the tribe, and other federal agencies review the application. It has to comply with NEPA. BLM leads on coal mining issues. The Native American regulatory authority or OSMRE leads on  reclamation and post-mining land use issues. The federal agencies and the tribe split enforcement depending on the activity:`}</p>
    <ul>
      <li parentName="ul">{`The tribal regulatory authority or OSMRE monitors, inspects, and enforces SMCRA permit requirements related to surface effects and reclamation of the area mined.`}</li>
      <li parentName="ul">{`BLM monitors, inspects, and enforces the mining plan requirements for coal development, production, and resource recovery.`}</li>
      <li parentName="ul">{`BIA monitors and enforces minerals agreement or lease terms, conditions, and stipulations not related to the mining plan or the SMCRA permit.`}</li>
    </ul>
    <p>{`OSMRE lists which tribes have their own regulatory programs and Abandoned Mine Land Reclamation programs. OSMRE `}<a parentName="p" {...{
        "href": "https://www.odocs.osmre.gov/"
      }}>{`provides links to their regulatory oversight documents `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. Currently, the Crow Tribe, Hopi Tribe, and Navajo Nation have their own regulatory programs.`}</p>
    <br />
When the operator decides to abandon mining, they must reclaim the mining area according to the SMCRA plan and permit. The Native American regulatory agency or OSMRE arranges for inspection of the reclaimed area. The operator, BIA, BLM, and tribal and surface owners also take part in the inspection. Public hearings and consultations are also held. The operator must take remedial action if necessary to further reclamation based on the review of agencies, the tribe, and individual Native American surface owners. When the area has been satisfactorily reclaimed, the bond is released.
    <br /><br />
Read more about <a href="/how-revenue-works/aml-reclamation-program/">OSMRE and the Abandoned Mine Land program</a>.
    <h2 {...{
      "id": "Renewable-energy",
      "style": {
        "position": "relative"
      }
    }}>{`Renewable energy`}</h2>
    <p>{`The Department of Energy (DOE) estimates that Native American lands comprise 5.8% of U.S. land and 6.5% of renewable energy potential. This includes 7.8% of the U.S. technical potential for wind and 5.4% for utility-scale photovoltaic solar.`}</p>
    <br />
A number of agencies within the Department of the Interior play a role in the process of developing renewable energy on Native American land:
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.bia.gov/"
        }}>{`Bureau of Indian Affairs `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (BIA)`}</li>
      <li parentName="ul">{`The Office of Indian Energy and Economic Development (IEED) and its `}<a parentName="li" {...{
          "href": "https://www.bia.gov/bia/ots/demd"
        }}>{`Division of Energy and Mineral Development `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (DEMD)`}</li>
      <li parentName="ul">{`The Department of Energy’s `}<a parentName="li" {...{
          "href": "https://energy.gov/indianenergy/office-indian-energy-policy-and-programs"
        }}>{`Office of Indian Energy Policy `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://www.blm.gov/"
        }}>{`Bureau of Land Management `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (BLM)`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://onrr.gov/"
        }}>{`Office of Natural Resources Revenue `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (ONRR)`}</li>
      <li parentName="ul">{`The Office of the Special Trustee for American Indians (OST)`}</li>
    </ul>
    <p>{`Key documents and resources:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.epa.gov/laws-regulations/summary-national-environmental-policy-act"
        }}>{`National Environmental Policy Act `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` (NEPA)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.nrel.gov/docs/fy13osti/57748.pdf"
        }}>{`Developing Clean Energy on Tribal Lands report (PDF) `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a></li>
      <li parentName="ul">{`DEMD resources:`}<ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/bia/ots/demd/renewable-energy"
            }}>{`Renewable Energy Opportunities `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/sites/bia.gov/files/assets/as-ia/ieed/ieed/pdf/idc010168.pdf"
            }}>{`Building Green Economies on Tribal Lands (PDF) `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.bia.gov/bia/ots/demd/renewable-energy/wind"
            }}>{`Information on tribes actively seeking wind development companies and investors `}<OpenInNewIcon style={{
                fontSize: 20
              }} mdxType="OpenInNewIcon" /></a></li>
        </ul></li>
    </ul>
    <p>{`BIA reviews and approves the development of renewable energy projects related to sun, wind, and biomass since these projects only involve surface land use. BLM and ONRR, while kept informed, do not have statutory authority to review or approve. OST may be involved in the accounting for and distribution of revenues from any leases or right-of-way agreements.`}</p>
    <br />
Geothermal energy resources constitute the one exception. Leases or agreements covering geothermal energy resources are treated as fluid minerals, much like <a href="#Oil-and-gas">oil and gas</a>.
    <br /><br />
The information in this section covers wind and solar leases.
    <h4 {...{
      "id": "Plan-3",
      "style": {
        "position": "relative"
      }
    }}>{`Plan`}</h4>
    <p>{`DEMD works with tribes and individuals to assess renewable energy resources, conduct feasibility studies, and identify business development opportunities. DEMD provides `}<a href="https://www.bia.gov/bia/ots/demd/renewable-energy">{`more information on renewable energy opportunities `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>. <a href="https://www.bia.gov/service/grant-assistance-energy-grants">The Energy and Mineral Development Program also provides funding for energy and mineral pre-development studies <OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <br />
DEMD provides <a href="https://www.bia.gov/sites/bia.gov/files/assets/as-ia/ieed/bia/pdf/idc1-031905.pdf">a broad overview of the process for developing renewable energy projects (PDF) <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>. DEMD works with the tribe to create an opportunity assessment and study the feasibility of energy development on Native American land.
    <br /><br />
During the planning phase, the tribe decides the path it will take to developing the renewable energy project. The tribe decides if it wants to develop and own the project, bring on a developer to own the project and pay the tribe, or partner with a developer. If a tribe or individual chooses to engage a developer, then the lease process is governed under <a href="https://www.ecfr.gov/cgi-bin/text-idx?SID=6ed5b1f8ed199e697388d20386609164&mc=true&tpl=/ecfrbrowse/Title25/25cfr162_main_02.tpl">25 CFR Part 162, Subpart E – Wind and Solar Resource Leases <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>. Other laws can govern the development as well. If the project is part of another lease (such as an agricultural, residential, or business lease), it is included in that lease review under another subpart of <a href="https://www.ecfr.gov/cgi-bin/text-idx?SID=6ed5b1f8ed199e697388d20386609164&mc=true&tpl=/ecfrbrowse/Title25/25cfr162_main_02.tpl">25 CFR Part 162 <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>. If the tribe is developing the project on Native American land, other federal laws (such as environmental laws) may still apply even though the tribe does not need BIA approval.
    <h4 {...{
      "id": "Explore-3",
      "style": {
        "position": "relative"
      }
    }}>{`Explore`}</h4>
    <p>{`A more detailed evaluation of the energy potential is necessary for some projects, particularly for wind. If a developer other than the tribe will be conducting a wind energy evaluation, they must get a separate wind energy evaluation lease (WEEL). These short-term leases allow the developer to install, operate, and maintain infrastructure for the wind resource evaluation. BIA must approve these leases, which have an initial term of no more than three years. `}</p>
    <p>{`The lease applications must include:`}</p>
    <ul>
      <li parentName="ul">{`compensation for the tribe or individual landowner`}</li>
      <li parentName="ul">{`tribal authorization (if on tribal land)`}</li>
      <li parentName="ul">{`acknowledgement that the lease complies with applicable tribal law`}</li>
      <li parentName="ul">{`site assessments`}</li>
      <li parentName="ul">{`environmental and archaeological reports `}</li>
    </ul>
    <p>{`The lease must stipulate the equipment installation plan and a restoration and reclamation plan. WEELs do not need a performance bond. The tribe or individual owns the energy resource information created by the project.`}</p>
    <h4 {...{
      "id": "Lease-3",
      "style": {
        "position": "relative"
      }
    }}>{`Lease`}</h4>
    <p>{`If a tribe or individual chooses to develop a full wind or solar energy project with a developer, they move forward with a wind and solar resource lease (WSR). These leases allow possession of Native American land for the purpose of installing, operating, and maintaining instrumentation, facilities, and associated infrastructure, such as wind turbines and solar panels.`}</p>
    <br />
BIA must approve a WSR lease, unless they have already approved the tribe to negotiate and approve leases under the <a href="https://www.bia.gov/service/hearth-leasing">HEARTH Act <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>.
    <br /><br />
Generally, a WSR may not exceed fifty years (a 25-year base term with a 25-year option), though exceptions exist. BIA defers to a tribe’s determination of the lease term. The payments under a WSR must be satisfactory to the tribe. If the project will be on tribal land, it may request a valuation to determine the fair market rental. If the project will be on individual trust land, a valuation is usually required. An economic analysis from IEED may substitute for a valuation. The payments made under a WSR may go directly to Native American landowners or through BIA. The tribe or individual may have the option of receiving in-kind payments. For BIA to approve a WSR, the application must include, among other items, the following:
    <ul>
      <li parentName="ul">{`tribal authorization for the project (if on tribal land)`}</li>
      <li parentName="ul">{`any valuation`}</li>
      <li parentName="ul">{`proof of insurance`}</li>
      <li parentName="ul">{`a performance bond`}</li>
      <li parentName="ul">{`a statement that the project is in conformance with applicable tribal law`}</li>
      <li parentName="ul">{`environmental and archaeological reports`}</li>
      <li parentName="ul">{`a resource development plan`}</li>
      <li parentName="ul">{`a restoration and reclamation plan`}</li>
      <li parentName="ul">{`proof of technical capability (unless the developer is owned by the tribe) `}</li>
    </ul>
    <p>{`The review of the application occurs before or during the NEPA review and any valuation.`}</p>
    <h4 {...{
      "id": "Develop-decommission-and-reclaim-3",
      "style": {
        "position": "relative"
      }
    }}>{`Develop, decommission, and reclaim`}</h4>
    <p>{`Under a WSR, a developer must commence construction of the infrastructure within two years and maintain all equipment. A developer must provide a performance bond or security no less than:`}</p>
    <ol>
      <li parentName="ol">{`The highest annual rental specified in the lease. If compensation is not paid annually, another previously-established amount is used.`}</li>
      <li parentName="ol">{`Installation of any required permanent improvements.`}</li>
      <li parentName="ol">{`Operation and maintenance charges for any land within an irrigation project.`}</li>
      <li parentName="ol">{`Restoration and reclamation of leased premises.`}</li>
    </ol>
    <p>{`The WSR must stipulate what will happen to the infrastructure after the lease term, including who will own it and whether it will remain on the land.`}</p>
    <h2 {...{
      "id": "Production-on-Native-American-land",
      "style": {
        "position": "relative"
      }
    }}>{`Production on Native American land`}</h2>
 <Link href='/query-data/?dataType=Production&landType=Native%20American' linkType='FilterTable' mdxType="Link">Query production data</Link>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      